import { useEffect } from 'react';

/**
 * Candidate Portal Analytics Component - GTM Only
 *
 * Loads Google Tag Manager (GTM-NM9LGHBW) on all production subdomains
 * GA4 is managed via GTM Web UI for:
 * - Centralized tracking configuration
 * - Improved performance (single script dependency)
 * - Easier tag management without code deployment
 * - Better debugging via GTM Preview/Debug mode
 *
 * Domain Guard: Blocks analytics on dev.*, staging.*, and S3 URLs
 * Examples that FIRE: infosys.qualifyme.ai, wipro.qualifyme.ai, avadhuta.qualifyme.ai
 * Examples that DON'T FIRE: dev.candidate.qualifyme.ai, staging.*, *.s3-website.*
 */

// Extend Window interface for proper TypeScript support
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown> | unknown[]>;
    gtag: (...args: unknown[]) => void;
  }
}

const GTM_ID = 'GTM-NM9LGHBW';

/**
 * Check if current environment allows analytics
 * Blocks dev, staging, and S3 URLs; allows all other subdomains
 */
function isAnalyticsAllowed(): boolean {
  const hostname = window.location.hostname;

  // Block analytics on development, staging, and S3 deployment URLs
  const isBlockedEnvironment =
    hostname.includes('dev.') ||
    hostname.includes('staging.') ||
    hostname.includes('s3-website');

  return !isBlockedEnvironment;
}

/**
 * Initialize the dataLayer array and gtag function stub
 * Must run BEFORE GTM script loads to capture early events
 */
function initializeDataLayer(): void {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  // gtag stub: queues calls until GTM/GA4 library loads
  if (!window.gtag) {
    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }
}

/**
 * Load GTM script with idempotency check
 * Prevents duplicate script injection if component re-mounts
 */
function loadGTMScript(): void {
  // Check if GTM script already loaded (prevents re-injection)
  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js"]`)) {
    return;
  }

  // Push gtm.start event to dataLayer before script loads
  // This timestamp is used by GTM for performance tracking
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Inject GTM script
  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.id = 'gtm-script'; // For easier debugging
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

  // Append to head (GTM best practice)
  document.head.appendChild(gtmScript);
}

/**
 * Main Analytics component
 * Mounts once on app initialization to set up GTM tracking
 */
export const Analytics = () => {
  useEffect(() => {
    // Domain guard: Only initialize on allowed environments
    if (!isAnalyticsAllowed()) {
      return;
    }

    // Step 1: Initialize dataLayer and gtag stub
    initializeDataLayer();

    // Step 2: Load GTM script (with idempotency check)
    loadGTMScript();

    // Cleanup (optional): No cleanup needed as GTM persists for page lifetime
    // GTM listeners will continue to work for navigation tracking
  }, []);

  return null;
};
